import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';

import { InstallationsMigrationState, InstallationsMigrationStore } from '@store';

import { ImmediateStateMatcher } from '@common';
import { TrimLengthValidator } from '../../../../shared/validators/trim-length/trim-length-validator';
import { TrimPatternValidator } from '../../../../shared/validators/trim-pattern/trim-pattern-validator';
import { InstallationMigrationModalComponent } from '../installation-migration-modal/installation-migration-modal.component';
import { CustomerNumberFormGroup } from './installation-migration-customer-ids-modal.helper';

@Component({
  selector: 'app-installation-migration-customer-ids',
  templateUrl: './installation-migration-customer-ids-modal.component.html',
  styleUrls: ['./installation-migration-customer-ids-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationMigrationCustomerIdsModalComponent implements OnInit, OnDestroy {
  @Output() nextStep = new EventEmitter();
  formGroup = new FormGroup<CustomerNumberFormGroup>(
    {
      customerNumber: new FormControl<string | null>(null, [
        Validators.required,
        TrimLengthValidator(10),
        TrimPatternValidator('^[0-9]*$'),
      ]),
      newCustomerNumber: new FormControl<string | null>(null, [
        Validators.required,
        TrimLengthValidator(10),
        TrimPatternValidator('^[0-9]*$'),
      ]),
    },
    { validators: this.validateCustomerNumbersDifference }
  );

  customerCompanyName: string;
  newCustomerCompanyName: string;
  matcher = new ImmediateStateMatcher();

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private installationsMigrationStore: InstallationsMigrationStore,
    private dialogRef: MatDialogRef<InstallationMigrationModalComponent>,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initFormGroup();

    this.installationsMigrationStore.state$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((state) => state.isCustomerNumbersVerified)
      )
      .subscribe((state: InstallationsMigrationState) => {
        this.customerCompanyName = state.customerCompanyName;
        this.newCustomerCompanyName = state.newCustomerCompanyName;
        this.submitForm();
      });
  }

  onClose(): void {
    this.installationsMigrationStore.clearCachedData();
    this.dialogRef.close();
  }

  onSearchForInstallations(): void {
    const customerNumber = this.formGroup?.value?.customerNumber.trim();
    const newCustomerNumber = this.formGroup?.value?.newCustomerNumber.trim();

    this.installationsMigrationStore.setCustomerNumber(customerNumber);
    this.installationsMigrationStore.setNewCustomerNumber(newCustomerNumber);
    this.installationsMigrationStore.getCustomerCompanyNames(customerNumber, newCustomerNumber);
  }

  private initFormGroup(): void {
    this.installationsMigrationStore.state$
      .pipe(first())
      .subscribe((state: InstallationsMigrationState) => {
        const customerNumber = state.customerNumber;
        const newCustomerNumber = state.newCustomerNumber;

        this.formGroup.patchValue({
          customerNumber,
          newCustomerNumber,
        });
      });
  }

  private submitForm(): void {
    if (this.customerCompanyName && this.newCustomerCompanyName) {
      this.nextStep.emit();
    }

    if (!this.customerCompanyName) {
      this.formGroup.controls.customerNumber.setErrors({ notCorrect: true });
    }

    if (!this.newCustomerCompanyName) {
      this.formGroup.controls.newCustomerNumber.setErrors({ notCorrect: true });
    }

    this.cdr.detectChanges();
  }

  private validateCustomerNumbersDifference(
    formGroup: FormGroup<CustomerNumberFormGroup>
  ): ValidationErrors {
    const customerNumber: string = formGroup?.value?.customerNumber
      ? formGroup.value.customerNumber.trim()
      : '';
    const newCustomerNumber: string = formGroup?.value?.newCustomerNumber
      ? formGroup.value.newCustomerNumber.trim()
      : '';

    if (customerNumber && newCustomerNumber) {
      return customerNumber === newCustomerNumber ? { sameValues: true } : null;
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
